import './index.css'
import clsx from 'clsx'
import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { Track } from 'types/track'
import type { TrackOptions } from '@disco/state-management/actions/MediaActions'
import {
  addAggregatedTrackToDisco,
  toggleTrackInLibrary,
} from '@disco/state-management/actions/ExplorerActions'
import { isAggregatedSearchTrack, isReadOnlyTrack } from '@disco/utils/tracks'
import mixpanel from '@disco/api/mixpanel'
import { AUDIO_FILE, DRM_FILE, VIDEO_FILE } from '@disco/constants/FileTypes'
import { PLAYLIST } from '@disco/constants/ItemTypes'
import {
  NOT_WATERMARKED,
  WATERMARKED,
} from '@disco/constants/WatermarkStatusesTypes'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import {
  getTrackTextSettings,
  getUser,
} from '@disco/state-management/selectors/user'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { formatTime } from '@disco/utils/time-utils'
import { getTrackFormats } from '@disco/utils/tracks'
import ActionStar from 'components/common/action-star'
import ButtonContainer from 'components/ui/button-container'
import TrackArtwork from 'components/common/tracks/track-artwork'
import WatermarkingStatusFlag from 'components/explorer/watermarking-status-flag'
import Lozenge from 'components/ui/lozenge'
import PreRelease from 'components/ui/pre-release'
import MediaControl from '../media-control'
const MEDIA_TYPES = [AUDIO_FILE, VIDEO_FILE]
type Props = {
  action: React.ReactNode | null | undefined
  playbackContext: TrackOptions
  track: Track
  prevPane: Record<string, any> | null | undefined
}

const TrackDetails = ({ action, playbackContext, track, prevPane }: Props) => {
  const dispatch = useDispatch()
  const isPhone = useSelector(isDeviceTypePhone)
  const textSettings = useSelector(getTrackTextSettings)
  const user = useSelector(getUser)
  const readOnlyTrack = isReadOnlyTrack(track)
  const isStarred = useMemo(() => {
    if (!user || !user.groups[1]?.url) return false
    return track.groups.includes(user.groups[1].url)
  }, [track, user])
  const handleFavoriteClick = useCallback(() => {
    if (readOnlyTrack) {
      dispatch(addAggregatedTrackToDisco({ track }))
    } else {
      dispatch(toggleTrackInLibrary(track))
    }
  }, [dispatch, readOnlyTrack, track])

  const title = track.name || track.filename
  const fileType = getFileTypeByExt(track.ext)
  const artwork = <TrackArtwork track={track} />
  const isPlaybackAllowed =
    !track.outside_your_territories || fileType !== DRM_FILE
  const isMediaFileType = MEDIA_TYPES.includes(fileType)
  let watermarking_status = track.watermarking_status

  if (prevPane && prevPane.type === PLAYLIST) {
    if (prevPane.watermarking_status !== track.watermarking_status) {
      if (track.watermarking_status === WATERMARKED) {
        watermarking_status = NOT_WATERMARKED
      }
    }
  }

  return (
    <div className={'TrackDetails'}>
      <div className="TrackDetails-groups">
        {!isPhone && (
          <div className="TrackDetails-group TrackDetails-group--compact">
            <div className="TrackDetails-artwork TrackDetails-artwork--compact">
              {artwork}
            </div>
            <div className="TrackDetails-content TrackDetails-content--compact">
              <div
                className="TrackDetails-title overflow-ellipsis"
                title={title}
              >
                {title}
              </div>
              <div className="TrackDetails-meta overflow-ellipsis">
                {track.artist}
              </div>
            </div>
          </div>
        )}
        <div className="TrackDetails-group TrackDetails-group--full">
          <div className="TrackDetails-artwork-row">
            <div className="TrackDetails-artwork TrackDetails-artwork--full">
              {isMediaFileType && isPlaybackAllowed ? (
                <MediaControl
                  track={track}
                  mediaSource="in-app"
                  playbackContext={playbackContext}
                >
                  {artwork}
                </MediaControl>
              ) : (
                artwork
              )}
            </div>
            {!isPhone && (
              <div className="TrackDetails-action">
                <ButtonContainer>
                  {track.permissions?.manage && (
                    <ActionStar
                      active={isStarred}
                      onClick={handleFavoriteClick}
                      actionKind={readOnlyTrack ? 'import' : 'star'}
                    />
                  )}
                  {action}
                </ButtonContainer>
              </div>
            )}
          </div>
          <div className="TrackDetails-content TrackDetails-content--full">
            <div className="TrackDetails-title overflow-ellipsis" title={title}>
              {title}
            </div>
            <div className="TrackDetails-meta">
              <div className="overflow-ellipsis">{track.album}</div>
              <div className="overflow-ellipsis">
                {textSettings === 'default'
                  ? track.artist
                  : track.metadata?.Composer}
              </div>

              {track.origin?.source_library &&
                isAggregatedSearchTrack(track) && (
                  <div className="overflow-ellipsis">
                    From:{' '}
                    <a
                      href={track.origin.source_library.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-link"
                      onClick={() => {
                        // Properties for event #8 from https://disco-team.atlassian.net/browse/DISCO-5985
                        mixpanel.sendPureEvent('Click link', {
                          'Track ID': track.id,
                          Catalog: track.origin?.source_library?.id,
                          'Catalog Business': track.origin?.business?.id,
                          Type: 'Originating catalog',
                          Context: 'Aggregated search',
                        })
                      }}
                    >
                      {track.origin.source_library.name}
                    </a>
                  </div>
                )}
            </div>
            <div className="TrackDetails-footer">
              {isMediaFileType && (
                <span className="TrackDetails-footer-item">
                  {formatTime(track.duration)}
                </span>
              )}
              {getTrackFormats(track).map(format => (
                <span className="TrackDetails-footer-item" key={format}>
                  <Lozenge text={format} />
                </span>
              ))}
              {track.metadata?.['Release Date'] && (
                <span className="TrackDetails-footer-item">
                  <PreRelease date={track.metadata?.['Release Date']} />
                </span>
              )}
              {track.watermarking_status && (
                <span className="TrackDetails-footer-item">
                  <WatermarkingStatusFlag
                    watermarking_status={watermarking_status}
                    type={track.type}
                    short
                  />
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default React.memo<Props>(TrackDetails)
